import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
  HubConnectionState,
} from "@microsoft/signalr";
import { useAuth } from "context/AuthContext";
import * as Styled from "./styles";
import { renderToString } from "react-dom/server";
import { format } from "date-fns";
import { PIN } from "./utils";

interface SignalRConnectionHook {
  data: any | null;
  connection: HubConnection | null;
}

type AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;

const useMarkersConnection = (): SignalRConnectionHook | any => {
  const { dealerId } = useAuth();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [lastMarker, setLastMarker] = useState<any>({});
  const [markers, setMarkers] = useState<AdvancedMarkerElement[]>([]);
  const [latInput, setLatInput] = useState<string>("");
  const [lngInput, setLngInput] = useState<string>("");
  const [isDriver, setIsDriver] = useState<boolean>(false);
  const [load, setLoad] = useState<boolean>(false);
  const [mapInitialState, setMapInitialState] = useState<boolean>(false);
  const [markerLibrary, setMarkerLibrary] = useState<any>(null);

  const mapDivRef = useRef<HTMLInputElement>(null);
  const [markersClick, setMarkersClick] = useState<AdvancedMarkerElement>();

  const requestAnimationFrameRef = useRef<number | null>(null);

  const isMountedRef = useRef(true);
  const isInitializingRef = useRef(false);
  const hubConnection = useRef<HubConnection | null>(null);

  const createMarkerContent = (iconUrl: string, title?: string) => {
    const markerDiv = document.createElement("div");
    markerDiv.innerHTML = `
      <img src="${iconUrl}" alt="${
      title || "marker"
    }" style="width: 32px; height: 32px;" />
    `;
    return markerDiv;
  };

  const animateMarker = useCallback(
    (
      marker: AdvancedMarkerElement,
      newLat: number,
      newLng: number,
      startTime: number,
    ) => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - startTime;
      const duration = 5000; // 5 seconds

      const progress = Math.min(1, elapsedTime / duration);

      const currentPosition = marker.position as google.maps.LatLngLiteral;
      const currentLat = currentPosition.lat;
      const currentLng = currentPosition.lng;

      const interpolatedLat = currentLat + (newLat - currentLat) * progress;
      const interpolatedLng = currentLng + (newLng - currentLng) * progress;

      const newPosition = {
        lat: interpolatedLat,
        lng: interpolatedLng,
      };

      marker.position = newPosition;

      if (progress < 1) {
        requestAnimationFrameRef.current = requestAnimationFrame(() =>
          animateMarker(marker, newLat, newLng, startTime),
        );
      }
    },
    [],
  );

  const handleLastMarker = useCallback(() => {
    if (lastMarker.driverId && markerLibrary) {
      if (map && markers?.length > 0) {
        const index = markers?.findIndex(
          (marker: any) => (marker as any).id === lastMarker?.driverId,
        );
        if (index !== -1) {
          const newLat = parseFloat(lastMarker.latitude);
          const newLng = parseFloat(lastMarker.longitude);

          if (!!newLat && !!newLng) {
            const marker = markers[index];
            const startTime = Date.now();

            if (requestAnimationFrameRef.current) {
              cancelAnimationFrame(requestAnimationFrameRef.current);
            }

            marker.id = lastMarker.driverId;

            animateMarker(marker, newLat, newLng, startTime);

            setLatInput("");
            setLngInput("");
            setLastMarker({});
          }
          setLastMarker({});
        } else {
          if (
            !!lastMarker.latitude &&
            !!lastMarker.longitude &&
            markerLibrary
          ) {
            const { AdvancedMarkerElement } = markerLibrary;

            const iconUrl = lastMarker?.iconPin ?? PIN.DRIVER;
            const markerContent = createMarkerContent(
              iconUrl,
              lastMarker.driverName,
            );

            const newMarker = new AdvancedMarkerElement({
              position: {
                lat: lastMarker.latitude,
                lng: lastMarker.longitude,
              },
              map,
              title: lastMarker.driverName,
              content: markerContent,
            });

            newMarker.id = lastMarker.driverId;
            newMarker.iconUrl = iconUrl;

            newMarker.addListener("click", () => {
              setMarkersClick(newMarker);
            });

            if (iconUrl === PIN.DRIVER) {
              const infoContent = renderToString(
                <Styled.InfoWindowContainer>
                  <Styled.TopInfoWindowContainer>
                    <Styled.DriverName>
                      {lastMarker?.driverName}
                    </Styled.DriverName>

                    <Styled.OnlineBadge>Disponível</Styled.OnlineBadge>
                  </Styled.TopInfoWindowContainer>

                  <Styled.LastUpdateText>
                    Última atualização:{" "}
                    {format(
                      new Date(lastMarker?.dateTime || ""),
                      "dd/MM 'às' HH:mm",
                    )}
                  </Styled.LastUpdateText>
                </Styled.InfoWindowContainer>,
              );

              const infowindow = new window.google.maps.InfoWindow({
                content: infoContent,
              });

              const markerElement = newMarker.element;
              if (markerElement) {
                markerElement.addEventListener("mouseenter", () =>
                  infowindow.open(map, newMarker),
                );
                markerElement.addEventListener("mouseleave", () => {
                  infowindow.close();
                });
              }
            }

            setMarkers((prev: any) => [...prev, newMarker]);

            setLastMarker({});
          }
          setLastMarker({});
        }
      }
    }
  }, [markers, map, animateMarker, lastMarker, markerLibrary]);

  useEffect(() => {
    handleLastMarker();
  }, [handleLastMarker]);

  const closeConnection = useCallback(async () => {
    if (
      hubConnection.current &&
      hubConnection.current.state === HubConnectionState.Connected
    ) {
      try {
        hubConnection.current.off("LastDriverLocation");

        await hubConnection.current.stop();
      } catch (error) {
        console.error("error ao fechar a conexão", error);
      } finally {
        hubConnection.current = null;
      }
    }
  }, []);

  const initializeConnection = useCallback(async () => {
    if (isInitializingRef.current || !isMountedRef.current || !dealerId) {
      return;
    }

    isInitializingRef.current = true;

    try {
      await closeConnection();

      hubConnection.current = new HubConnectionBuilder()
        .withUrl(
          `${process.env.REACT_APP_LOGISTICS_API_URL}hubs/driver?dealerId=${dealerId}`,
          {
            withCredentials: false,
          },
        )
        .configureLogging(
          process.env.NODE_ENV === "development"
            ? LogLevel.Information
            : LogLevel.Error,
        )
        .withAutomaticReconnect([
          0, 3000, 5000, 10000, 15000, 30000, 60000, 120000,
        ])
        .build();

      hubConnection.current.onreconnected(() => {});

      hubConnection.current.onclose(async () => {
        if (isMountedRef.current) {
          await initializeConnection();
        }
      });

      hubConnection.current.on("LastDriverLocation", (newData) => {
        setLastMarker(newData);
      });

      await hubConnection.current.start();
    } catch (error) {
      console.error("error ao iniciar o SignalR:", error);

      const retryDelay = Math.min(5000 * 2 ** Math.random(), 30000);

      setTimeout(() => {
        if (isMountedRef.current) {
          initializeConnection();
        }
      }, retryDelay);
      return;
    } finally {
      isInitializingRef.current = false;
    }
  }, [dealerId, closeConnection]);

  useEffect(() => {
    isMountedRef.current = true;

    initializeConnection();

    return () => {
      isMountedRef.current = false;
      closeConnection();
    };
  }, [closeConnection, dealerId, initializeConnection]);

  return {
    hubConnection,
    map,
    setMap,
    markers,
    setMarkers,
    latInput,
    setLatInput,
    lngInput,
    setLngInput,
    load,
    setLoad,
    mapDivRef,
    mapInitialState,
    setMapInitialState,
    setLastMarker,
    isDriver,
    markersClick,
    setMarkersClick,
    setIsDriver,
    markerLibrary,
    setMarkerLibrary,
  };
};

export default useMarkersConnection;
